import { useCallback, useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import {
  Box,
  Button,
  DataTableColumnSettings,
  Icon,
  Text,
  toastManager,
} from "@salestrak/ui";
import { PageHeader } from "#app/components/page-header";
import { ExpandableSearch } from "@salestrak/ui";
import {
  WorkflowsTable,
  useWorkflowColumns,
} from "#app/components/workflows/workflows-table";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import { DeleteWorkflowDialog } from "#app/components/workflows/delete-workflow-dialog";
import { CreateWorkflowDialog } from "#app/components/workflows/create-workflow-dialog";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { useListQueryState } from "#app/lib/hooks/use-list-query-state";
import { WORKFLOW_LIST } from "#app/lib/filters/list-configs";
import type { WorkflowRow } from "#app/lib/data";
import { useWorkflowMutations } from "#app/lib/hooks/use-workflow-mutations";
import { useAppData } from "#app/lib/data";
import { usePermission } from "#app/lib/hooks/use-permission";
import { withPermission } from "#app/components/permissions/require-permission";
import type { Omittable } from "@salestrak/ui";


function WorkflowsPage() {
  const { create } = Route.useSearch();
  const navigate = useNavigate();
  const { push } = useAppNavigate();

  const {
    searchValue,
    setSearchValue,
    sorting,
    setSorting,
    where,
    orderBy,
  } = useListQueryState(WORKFLOW_LIST);

  const { setDefaultWorkflow } = useWorkflowMutations();
  const { refetch: refetchAppData } = useAppData();
  const canCreateWorkflows = usePermission("create.workflows");

  // Dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WorkflowRow | null>(null);

  // Open create dialog when navigated with ?create=true — but only with the
  // create permission, so a stale/forged link can't open the dialog. The
  // param is always cleared so it doesn't re-fire.
  useEffect(() => {
    if (create === "true") {
      if (canCreateWorkflows) setCreateOpen(true);
      navigate({ to: ".", search: (prev) => ({ ...prev, create: undefined }) });
    }
  }, [create, canCreateWorkflows, navigate]);

  // Handlers
  const navigateToWorkflow = useCallback(
    (workflowId: string) => {
      push(`/settings/workflows/${workflowId}`);
    },
    [push],
  );

  const handleCreated = useCallback(
    (workflowId: string) => {
      refetchAppData();
      navigateToWorkflow(workflowId);
    },
    [refetchAppData, navigateToWorkflow],
  );

  const handleSetDefault = useCallback(
    async (workflow: WorkflowRow) => {
      try {
        await setDefaultWorkflow(workflow.workflowId);
        toastManager.add({ title: "Workflow set as default", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to set default workflow");
      }
    },
    [setDefaultWorkflow],
  );

  const openCreateDialog = useCallback(() => setCreateOpen(true), []);

  const clearDeleteTarget = useCallback(
    (open: boolean) => { if (!open) setDeleteTarget(null); },
    [],
  );

  const handleDeleted = useCallback(() => {
    setDeleteTarget(null);
    refetchAppData();
  }, [refetchAppData]);

  const columns = useWorkflowColumns(navigateToWorkflow);
  const tableView = useTableViewSettings("workflows");

  return (
    <Box fill="flex">
      <PageHeader
        title="Workflows"
        actions={
          <>
            <ExpandableSearch
              onValueChange={setSearchValue}
              placeholder="Search workflows..."
              size="xs"
            />
            <DataTableColumnSettings
              columns={columns}
              {...tableView.settingsProps}
            />
            {canCreateWorkflows && (
              <Button size="xs" onClick={openCreateDialog}>
                <Icon name="plus" />
                <Text>Add workflow</Text>
              </Button>
            )}
          </>
        }
      />
      <WorkflowsTable
        where={where}
        orderBy={orderBy}
        sorting={sorting}
        onSortingChange={setSorting}
        searchValue={searchValue}
        columns={columns}
        tableViewProps={tableView.tableProps}
        onEdit={navigateToWorkflow}
        onSetDefault={handleSetDefault}
        onRequestDelete={setDeleteTarget}
      />

      {canCreateWorkflows && (
        <CreateWorkflowDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={handleCreated}
        />
      )}

      <DeleteWorkflowDialog
        workflow={deleteTarget}
        open={!!deleteTarget}
        onOpenChange={clearDeleteTarget}
        onDeleted={handleDeleted}
        onManageWorkflow={navigateToWorkflow}
      />
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/workflows/")({
  validateSearch: (
    search: Record<string, unknown>,
    // A search param is written back as an explicit `undefined` to clear it —
    // the router reads that as "absent from the URL" — so the members relax.
  ): Omittable<{ create?: string }> => ({
    create: typeof search.create === "string" ? search.create : undefined,
  }),
  component: withPermission("manage.edit_workflows", WorkflowsPage),
});
